// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable no-prototype-builtins */
import QuoteLineImageCarousel from "@bg/App/Components/RfqQuoteDetails/QuoteLineImageCarousel";
import RelatedQuoteLine from "@bg/App/Components/RfqQuoteDetails/SalesRepRelatedQuoteLine";
import setGroupWishListLineIsSelected from "@bg/App/Handlers/Pages/MyListDetails/SetGroupSelectedLines";
import deleteLines from "@bg/App/Handlers/RequestQuote/DeleteLines";
import deleteRelatedLine from "@bg/App/Handlers/RequestQuote/DeleteRelatedLine";
import updateLineInfo from "@bg/App/Handlers/RequestQuote/UpdateLineInfo";
import { linkIconStyleGenerator } from "@bg/App/SharedStyles/GeneralStyles";
import { CustomAppState } from "@bg/App/Store/Reducers";
import { LinkPresentationProps } from "@bg/MobiusOverrides/Link/Link";
import ProductAvailability from "@bg/Overrides/Components/ProductAvailability";
import ProductDescription from "@bg/Overrides/Components/ProductDescription";
import TwoButtonModal from "@bg/Overrides/Components/TwoButtonModal";
import { productListLineStyles } from "@bg/Overrides/Widgets/MyListsDetails/MyListDetailsProductListLineStyles";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import xScreen from "@bg/XDL/Utilities/xScreen";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import translate from "@insite/client-framework/Translate";
import { QuoteLineModel, QuoteModel } from "@insite/client-framework/Types/ApiModels";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import RfqQuoteDetailsQuotedPricing from "@insite/content-library/Widgets/RfqQuoteDetails/RfqQuoteDetailsQuotedPricing";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import Icon from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { ChangeEvent, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    selectedGroupLines: state.commonActions["selectedGroupLines"],
    isQuoteDisabled: state.commonActions.isQuoteDisabled,
});

const mapDispatchToProps = {
    setGroupWishListLineIsSelected,
    deleteLines,
    removeCartLine,
    deleteRelatedLine,
    updateLineInfo,
};

export interface SalesRepLinesStyles {
    quantityGridItem?: InjectableCss;
    quotedPricingGridItem?: InjectableCss;
    quoteButtonGrid?: InjectableCss;
    quoteLineButton?: ButtonPresentationProps;
    addRelatedProductLink?: LinkPresentationProps;
    notesGridItem?: InjectableCss;
    relatedItemsWrapper?: InjectableCss;
    removeQuoteLineLink?: LinkPresentationProps;
    toggleNotes?: LinkPresentationProps;
    notesWrapper?: InjectableCss;
    editableNotesTextArea?: TextAreaProps;
}

export const styles: SalesRepLinesStyles = {
    quantityGridItem: {
        css: css`
            min-width: calc((100% - 150px) / 3);
            text-align: center;
            @media (max-width: 600px) {
                width: 50%;
                text-align: left;
                order: 2;
            }
        `,
    },
    quotedPricingGridItem: {
        css: css`
            flex-grow: 1;
            @media (max-width: 600px) {
                width: 100%;
                order: 1;
            }
        `,
    },
    quoteButtonGrid: {
        css: css`
            min-width: 150px;
            display: flex;
            justify-content: flex-end;
            align-items: flex-start;
            @media (max-width: 600px) {
                width: 50%;
                min-width: unset;
                order: 3;
            }
        `,
    },
    quoteLineButton: {
        css: css`
            &&&& {
                background: ${getColor("primary")};
                color: ${getColor("common.white")};
                &&:hover {
                    background: ${getColor("common.white")};
                    color: ${getColor("primary")};
                    border-color: ${getColor("primary")};
                }
            }
        `,
    },
    addRelatedProductLink: linkIconStyleGenerator(
        "Search",
        "13px",
        14,
        `line-height: ${fontConfig.lineHeight.singleLine};`,
    ),
    removeQuoteLineLink: linkIconStyleGenerator(
        "Delete",
        "13px",
        14,
        `line-height: ${fontConfig.lineHeight.singleLine};`,
    ),
    notesGridItem: {
        css: css`
            width: 100%;
            margin: 10px 0 0;
            display: flex;
            justify-content: space-between;
            ${xScreen("lte", "sm")} {
                flex-direction: column;
                gap: 10px;
                align-items: flex-end;
            }
        `,
    },
    relatedItemsWrapper: {
        css: css`
            &&&.last-item .related-item:last-child {
                border-bottom: 3px solid ${getColor("common.ui3")};
            }
        `,
    },
    toggleNotes: {
        css: css`
            font-size: ${fontConfig.fontSize.xSmall}px;
        `,
        typographyProps: {
            size: fontConfig.fontSize.xSmall,
        },
        icon: {
            iconProps: {
                src: "Create",
                size: 14,
                color: "secondary.main",
                css: css`
                    margin-right: 4px;
                `,
            },
            position: "left",
        },
    },
    notesWrapper: {
        css: css`
            text-align: right;
            ${xScreen("lte", "sm")} {
                width: 100%;
            }
        `,
    },
    editableNotesTextArea: {
        cssOverrides: {
            inputSelect: css`
                resize: none;
                padding: 8px;
                font-size: ${fontConfig.fontSize.xSmall}px;
            `,
        },
    },
};

interface OwnProps {
    quoteLine: QuoteLineModel;
    // used to obtain related products
    lines: QuoteLineModel[] | null;
    quote: QuoteModel;
    quoteLineClickHandler: (line: QuoteLineModel) => void;
    addRelatedProduct: (lineId: string, group: any) => void;
    groupName: string;
    // used to determine if the item is the last on this group for related products styling
    group?: any;
    total: number;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const SalesRepLines: React.FC<Props> = ({
    quoteLine,
    quote,
    quoteLineClickHandler,
    selectedGroupLines,
    groupName,
    setGroupWishListLineIsSelected,
    addRelatedProduct,
    lines,
    group,
    deleteLines,
    removeCartLine,
    deleteRelatedLine,
    total,
    isQuoteDisabled,
    updateLineInfo,
}) => {
    const [lineGroup, setLineGroup] = useState<any>();
    const [lineGroups, setLineGroups] = useState<any>();
    const [deleteLineModalIsOpen, setDeleteLineModalIsOpen] = useState(false);
    const [relatedItems, setRelatedItems] = useState<QuoteLineModel[]>([]);
    const [lineImages, setLineImages] = useState<any>();
    const [isDeleting, setIsDeleting] = useState(false);
    const [notes, setNotes] = useState(quoteLine?.notes);
    const [showNotes, setShowNotes] = useState(!!quoteLine.notes);
    const [quoteLineState, setQuoteLineState] = useState<QuoteLineModel>();
    useEffect(() => {
        if (quoteLine && quoteLine?.properties && quoteLine?.properties?.groups) {
            setLineGroups(JSON.parse(quoteLine.properties.groups));
            setLineGroup(
                JSON.parse(quoteLine.properties.groups)?.filter(
                    (x: any) => x.groupName === groupName.toLowerCase(),
                )?.[0],
            );
        }
        if (quoteLine && quoteLine?.properties) {
            let images = [] as any;
            if (quoteLine.properties.images) {
                images = JSON.parse(quoteLine.properties["images"]);
            } else {
                images = [{ id: quoteLine.productId, imagePath: quoteLine.smallImagePath }];
            }
            setLineImages(images);
        }
    }, [groupName, quoteLine]);

    useEffect(() => {
        if (lineGroup && lineGroup?.hasOwnProperty("relatedLinesIds") && lineGroup?.relatedLinesIds?.length && lines) {
            const erps: string[] = lineGroup.relatedLinesIds.split(",");
            const relatedProducts = lines.filter(
                line => erps.some(x => x === line.id) && line.properties.isRelatedProduct === "true",
            );
            setRelatedItems(relatedProducts);
        }
        /**
         * BG-2524
         * Support add line notes for sales rep
         * As quoteLine repurposing notes property for group note
         * So we have to find the current line from lines props
         * and set a local state for Notes
         */
        const currentLine = lines?.find(l => l.id === quoteLine.id);
        setQuoteLineState(currentLine);
        if (currentLine && !notes) {
            setNotes(currentLine.notes);
            setShowNotes(!!currentLine.notes);
        }
    }, [lineGroup, lines]);

    const toggleNotesClickHandler = () => {
        setShowNotes(!showNotes);
    };

    const notesChangeHandler = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setNotes(event.currentTarget.value);
    };

    const notesBlurHandler = () => {
        if (notes === quoteLineState?.notes) {
            return;
        }
        updateLineInfo({
            cartId: quote.id,
            originalGroupName: groupName,
            linesModel: [
                {
                    lineId: quoteLine.id,
                    qtyOrdered: lineGroup.qtyOrdered,
                    sortOrder: lineGroup.sortOrder,
                    notes,
                },
            ],
            reloadQuote: true,
        });
        setShowNotes(!!notes);
    };

    const toggleNotesHeading = showNotes
        ? translate("Hide Line Notes")
        : notes?.length === 0
        ? translate("Add Line Notes")
        : translate("Show Line Notes");

    const selectChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setGroupWishListLineIsSelected({
            wishListLineIds: [quoteLine.id],
            groupName,
        });
    };

    const removeQuoteLine = () => {
        if (relatedItems.length) {
            const linesModel = [] as any;
            relatedItems.forEach(line => {
                const relatedLine = {
                    lineId: line.id,
                    parentId: quoteLine.id,
                };
                linesModel.push(relatedLine);
            });
            setIsDeleting(true);

            deleteRelatedLine({
                cartId: quote.id,
                originalGroupName: groupName,
                linesModel,
                onComplete: (result: any) => {
                    if (result && result.successful) {
                        deleteLine();
                    }
                },
            });
        } else {
            deleteLine();
        }
    };

    const deleteLine = () => {
        if (lineGroups.length === 1 && lineGroups[0].groupName.toLowerCase() === groupName.toLowerCase()) {
            removeCartLine({
                cartId: quote.id,
                cartLineId: quoteLine.id,
                reloadQuote: true,
                isQuoteLine: true,
            } as any);
            setIsDeleting(false);
            setDeleteLineModalIsOpen(false);
        } else {
            deleteLines({
                cartId: quote.id,
                originalGroupName: groupName,
                newGroupName: "",
                linesModel: [{ lineId: quoteLine.id }],
                reloadCart: false,
                reloadQuote: true,
                onComplete: result => {
                    if (result.successful) {
                        setIsDeleting(false);
                        setDeleteLineModalIsOpen(false);
                        setGroupWishListLineIsSelected({
                            wishListLineIds: [quoteLine.id],
                            groupName,
                            clearOnly: true,
                        });
                    }
                },
            });
        }
    };

    const allowRemoveItems =
        quote.status === "QuoteCreated" || quote.status === "QuoteRequested" || quote.status === "QuoteRejected";

    return (
        <StyledWrapper>
            <StyledWrapper
                {...productListLineStyles.lineInnerContainer}
                className={`no-borders ${relatedItems.length > 0 ? "has-related-products" : ""} ${
                    lineGroup?.sortOrder === group.length ? "last-line" : ""
                }`}
                data-test-selector={`quoteLine_${quoteLine.productId}_${quoteLine.unitOfMeasure}`}
            >
                <StyledWrapper {...productListLineStyles.leftSection}>
                    <StyledWrapper {...productListLineStyles.imageGridItem}>
                        <StyledWrapper {...productListLineStyles.dragGrid}>
                            {isQuoteDisabled && !isQuoteDisabled.isDisabled && (
                                <>
                                    <Checkbox
                                        {...productListLineStyles.selectedCheckbox}
                                        checked={
                                            selectedGroupLines?.hasOwnProperty(groupName) &&
                                            selectedGroupLines[groupName].indexOf(quoteLine.id) > -1
                                        }
                                        onChange={selectChangeHandler}
                                        data-test-selector="selectItem"
                                    />
                                    <StyledWrapper {...productListLineStyles.dragIconWrapper}>
                                        <Icon {...productListLineStyles.dragHandleIcon} className="drag-handle" />
                                    </StyledWrapper>
                                </>
                            )}
                        </StyledWrapper>
                        <QuoteLineImageCarousel
                            productId={quoteLine.id}
                            productImages={lineImages}
                            productUrl={quoteLine.productUri}
                        />
                    </StyledWrapper>

                    <StyledWrapper {...productListLineStyles.descriptionGridItem}>
                        <ProductDescription
                            product={quoteLine}
                            lines={2}
                            lineHeight={22}
                            extendedStyles={productListLineStyles.productDescriptionStyles}
                        />
                        <StyledWrapper {...productListLineStyles.partNumbersGridItem}>
                            <ProductPartNumbers
                                productNumber={quoteLine.properties.sku || quoteLine.erpNumber}
                                manufacturerItem={quoteLine.manufacturerItem}
                                extendedStyles={productListLineStyles.productPartNumbersStyles}
                            />
                        </StyledWrapper>
                        <StyledWrapper {...productListLineStyles.availabilityGridItem}>
                            <ProductAvailability
                                productId={quoteLine.productId!}
                                availability={quoteLine.availability!}
                                unitOfMeasure={quoteLine.unitOfMeasure}
                                trackInventory={quoteLine.trackInventory}
                                extendedStyles={productListLineStyles.productAvailabilityStyles}
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>

                <StyledWrapper {...productListLineStyles.rightSection}>
                    <StyledWrapper {...productListLineStyles.rightSectionTop}>
                        <StyledWrapper {...productListLineStyles.priceAndAvailabilityInnerContainer}>
                            <StyledWrapper {...styles.quantityGridItem}>
                                <Typography weight={fontConfig.weight.semiBold}>
                                    <legend>{translate("Qty")}</legend>
                                    {quoteLine?.qtyOrdered || 1}
                                </Typography>
                            </StyledWrapper>
                            <StyledWrapper {...styles.quotedPricingGridItem}>
                                <RfqQuoteDetailsQuotedPricing quote={quote} quoteLine={quoteLine} />
                            </StyledWrapper>
                            <StyledWrapper {...styles.quoteButtonGrid}>
                                {quote.isEditable ? (
                                    <Button
                                        sizeVariant="small"
                                        {...styles.quoteLineButton}
                                        disabled={isQuoteDisabled && isQuoteDisabled.isDisabled}
                                        onClick={() => quoteLineClickHandler(quoteLine)}
                                    >
                                        {translate("Quote")}
                                    </Button>
                                ) : (
                                    <Typography {...productListLineStyles.priceTotal}>
                                        <legend>{translate("Total")}</legend>
                                        {quoteLine.properties["pricingFailed"]?.toLowerCase() === "true" ? (
                                            "N/A"
                                        ) : (
                                            <LocalizedCurrency amount={total} />
                                        )}
                                    </Typography>
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                    {quoteLine.properties["pricingFailed"]?.toLowerCase() === "true" && (
                        <StyledWrapper>
                            <Typography
                                color="danger"
                                size={fontConfig.fontSize.xSmall}
                                lineHeight={fontConfig.lineHeight.singleLineSmall}
                            >
                                {siteMessage("Custom_Price_FailedToLoad")}
                            </Typography>
                        </StyledWrapper>
                    )}

                    {quote.isEditable && isQuoteDisabled && !isQuoteDisabled.isDisabled && (
                        <StyledWrapper {...styles.notesGridItem}>
                            {allowRemoveItems ? (
                                <Link {...styles.removeQuoteLineLink} onClick={() => setDeleteLineModalIsOpen(true)}>
                                    {translate("Remove Item")}
                                </Link>
                            ) : (
                                <StyledWrapper></StyledWrapper>
                            )}

                            <Link
                                onClick={() => addRelatedProduct(quoteLine.id, lineGroup)}
                                {...styles.addRelatedProductLink}
                            >
                                {translate("Add Related Product")}
                            </Link>
                            <StyledWrapper {...styles.notesWrapper}>
                                <Link {...styles.toggleNotes} onClick={toggleNotesClickHandler}>
                                    {toggleNotesHeading}
                                </Link>
                                {showNotes && (
                                    <TextArea
                                        value={notes}
                                        onChange={notesChangeHandler}
                                        rows={2}
                                        maxLength={300}
                                        onBlur={notesBlurHandler}
                                        {...styles.editableNotesTextArea}
                                    />
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
            {relatedItems && relatedItems.length > 0 && (
                <StyledWrapper
                    {...styles.relatedItemsWrapper}
                    className={lineGroup.sortOrder === group.length ? "last-item" : ""}
                >
                    {relatedItems &&
                        relatedItems.length > 0 &&
                        relatedItems.map(line => (
                            <RelatedQuoteLine
                                key={`relatedQuoteLine_${line.id}`}
                                quote={quote}
                                parentLineId={quoteLine.id}
                                quoteLineClickHandler={quoteLineClickHandler}
                                groupName={groupName}
                                quoteLine={line}
                                isDisabled={isQuoteDisabled && isQuoteDisabled.isDisabled}
                            />
                        ))}
                </StyledWrapper>
            )}
            <TwoButtonModal
                modalIsOpen={deleteLineModalIsOpen}
                headlineText={translate("Delete List Item")}
                messageText={`${translate("Are you sure you want to delete this item?")}`}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Delete")}
                onCancel={() => setDeleteLineModalIsOpen(false)}
                onSubmit={removeQuoteLine}
                submitTestSelector="delteRelatedQuoteLine"
                showSubmitBtnLoadingOn={isDeleting}
            />
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(SalesRepLines);
